import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { MdStar } from "react-icons/md";
import { FaAngleDown } from "react-icons/fa6";
import { Helmet } from "react-helmet";
const API_BASE = "https://network.rentel.in";

/* ================= UTILS ================= */

const slugify = (text = "") =>
    text.toLowerCase().trim().replace(/\s+/g, "-");

const calculateDiscountedPrice = (price, discount) =>
    discount ? price - (price * discount) / 100 : price;

const capitalizeWords = (text = "") =>
    text
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ");

/* ================= COMPONENT ================= */

const SearchPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const searchQuery = new URLSearchParams(location.search).get("query");
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [openCategory, setOpenCategory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sortBy, setSortBy] = useState("");

    /* 🔥 MOBILE DETECTION */
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isVerySmall, setIsVerySmall] = useState(window.innerWidth <= 360);


    useEffect(() => {
        const resize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);



    useEffect(() => {
        const handleResize = () => {
            setIsVerySmall(window.innerWidth <= 360);
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    /* ================= FETCH PRODUCTS ================= */


    useEffect(() => {
        if (!searchQuery) return;

        setLoading(true);
        axios
            .get(`${API_BASE}/products/get-user-products?search=${searchQuery}`)
            .then((res) => {
                const all = res.data?.products || [];
                setProducts(
                    all,
                );
            })
            .finally(() => setLoading(false));
    }, [searchQuery]);


    /* ================= FETCH CATEGORIES ================= */

    useEffect(() => {
        axios
            .get("https://network.rentel.in/categories/get")
            .then((res) => setCategories(res.data?.categories || []));
    }, []);

    /* ================= SORT ================= */

    const finalProducts = useMemo(() => {
        let list = [...products];

        if (sortBy === "price_low_high") {
            list.sort(
                (a, b) =>
                    calculateDiscountedPrice(a.price, a.discount) -
                    calculateDiscountedPrice(b.price, b.discount)
            );
        }

        if (sortBy === "price_high_low") {
            list.sort(
                (a, b) =>
                    calculateDiscountedPrice(b.price, b.discount) -
                    calculateDiscountedPrice(a.price, a.discount)
            );
        }

        return list;
    }, [products, sortBy]);

    const capitalizeWords = (text = "") =>
        text
            .split(" ")
            .map(word =>
                word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : ""
            )
            .join(" ");

    return (
        <>
            <Helmet>
                <title>
                    {searchQuery
                        ? `Search "${searchQuery}" | Rentel`
                        : "Search | Rentel"}
                </title>

                <meta
                    name="description"
                    content={
                        searchQuery
                            ? `Find ${searchQuery} on Rentel rental marketplace`
                            : "Search products on Rentel rental marketplace"
                    }
                />
            </Helmet>

            <Header />

            <div className="container" style={{}}>
                {/* ================= DESKTOP VIEW ================= */}
                {!isMobile && (
                    <div style={{ display: "flex", gap: "20px", marginTop: "90px" }}>
                        {/* SIDEBAR */}
                        <aside
                            style={{
                                width: "260px",
                                border: "1px solid #eee",
                                borderRadius: "8px",
                                background: "#fff",
                                height: "calc(100%)",
                                display: "flex",
                                flexDirection: "column",
                                marginBottom: "30px",
                            }}
                        >
                            {/* TITLE */}
                            <h3
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "600",
                                    marginBottom: "12px",
                                    paddingBottom: "20px",
                                    paddingTop: "20px",
                                    textAlign: "center",
                                    borderBottom: "1px solid #f0f0f0",
                                }}
                            >
                                Filter By Category
                            </h3>

                            {/* SCROLL AREA */}
                            <div style={{ flex: 1, paddingRight: "6px" }}>
                                {categories.length > 0 ? (
                                    categories.map((cat) => {
                                        const hasSub = cat.subCategories && cat.subCategories.length > 0;
                                        const isOpen = openCategory === cat._id;

                                        return (
                                            <div key={cat._id} style={{ marginBottom: "10px", padding: "10px" }}>
                                                {/* CATEGORY ROW */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        fontWeight: "600",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        if (hasSub) {
                                                            setOpenCategory(isOpen ? null : cat._id);
                                                        } else {
                                                            navigate(`/category/${slugify(cat.name)}`);
                                                            setMobileFilterOpen(false);
                                                        }
                                                    }}
                                                >
                                                    <span>{cat.name}</span>
                                                    <span>{hasSub ? (isOpen ? "−" : "+") : ""}</span>
                                                </div>

                                                {/* SUBCATEGORIES */}
                                                {hasSub &&
                                                    isOpen &&
                                                    cat.subCategories.map((sub) => (
                                                        <div
                                                            key={sub}
                                                            style={{
                                                                marginTop: "6px",
                                                                padding: "8px 12px",
                                                                borderRadius: "8px",
                                                                cursor: "pointer",
                                                                fontSize: "14px",
                                                                fontWeight: "400",
                                                                backgroundColor: "#f7f7f7",
                                                                color: "#111",
                                                                border: "1px solid #eee",
                                                                transition: "all 0.2s ease",
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.backgroundColor = "#EA0F43";
                                                                e.currentTarget.style.color = "#fff";
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.backgroundColor = "#f7f7f7";
                                                                e.currentTarget.style.color = "#111";
                                                            }}
                                                            onClick={() =>
                                                                navigate(`/category/${slugify(cat.name)}/${slugify(sub)}`)
                                                            }
                                                        >
                                                            {sub.charAt(0).toUpperCase() + sub.slice(1).toLowerCase()}
                                                        </div>
                                                    ))}


                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ fontSize: "14px", color: "#777" }}>
                                        No categories found
                                    </div>
                                )}
                            </div>
                        </aside>


                        {/* CONTENT */}
                        <main style={{ flex: 1 }}>
                            {/* BACK + SORT */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "20px",
                                }}
                            >
                                <button
                                    onClick={() => navigate(-1)}
                                    style={{
                                        background: "#EA0F43",
                                        color: "#fff",
                                        border: "none",
                                        padding: "8px 16px",
                                        borderRadius: "6px",
                                        display: "flex",
                                        gap: "6px",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                >
                                    ← Back
                                </button>

                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    style={{
                                        padding: "8px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #ddd",
                                        minWidth: "180px",
                                    }}
                                >
                                    <option value="">Sort by</option>
                                    <option value="price_low_high">Price: Low to High</option>
                                    <option value="price_high_low">Price: High to Low</option>
                                </select>
                            </div>

                            {/* PRODUCTS */}
                            {loading ? (
                                <div style={{ textAlign: "center" }}>Loading...</div>
                            ) : finalProducts.length === 0 ? (
                                <div style={{ textAlign: "center" }}>No products found</div>
                            ) : (
                                <div className="product-grid">
                                    {finalProducts.map((item) => (
                                        <div className="product-card" key={item._id}>
                                            <div className="image-container">
                                                <Link
                                                    to={`/${slugify(item.category)}/${slugify(
                                                        item.subCategory
                                                    )}/${slugify(item.title.replaceAll('/', ' '))}/${item._id}`}
                                                >
                                                    <img
                                                        src={item.images?.[0] || "/fallback.jpg"}
                                                        alt={item.title}
                                                    />
                                                </Link>

                                                <div className="rating-badge-co">
                                                    <div className="rating-badgel">
                                                        <MdStar />
                                                        <h3>{item.averageRating}</h3>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="all-item">
                                                <p className="originale-priceo">
                                                    {`${item.userId?.name || ""} ${item.userId?.lastName || "Unknown Seller"
                                                        }`}
                                                </p>

                                                <div className="sprat">
                                                    <h3>{capitalizeWords(item.title)}</h3>
                                                </div>


                                                <p className="price-discounti">
                                                    <span className="final-priceoe">
                                                        ₹
                                                        {calculateDiscountedPrice(
                                                            item.price,
                                                            item.discount
                                                        ).toLocaleString("en-IN")}
                                                    </span>

                                                    {item.discount > 0 && (
                                                        <>
                                                            |
                                                            <span className="discounte">{item.discount}% OFF</span>
                                                        </>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </main>
                    </div>
                )}

                {/* ================= MOBILE VIEW ================= */}
                {isMobile && (
                    <div>


                        {/* FILTER + SORT ROW */}
                        <div
                            style={{
                                display: "flex",
                                gap: "6px",
                                margin: "12px 0 12px 0",
                                alignItems: "center",
                                marginLeft: isVerySmall ? "-6px" : "",
                            }}
                        >
                            <button
                                onClick={() => navigate(-1)}
                                style={{
                                    width: isVerySmall ? "40px" : "72px",
                                    height: "40px",
                                    background: "#EA0F43",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                    flexShrink: 0,
                                }}
                            >
                                {isVerySmall ? "←" : "← Back"}
                            </button>


                            {/* 📂 FILTER */}
                            <details
                                open={mobileFilterOpen}
                                onToggle={(e) => setMobileFilterOpen(e.target.open)}
                                style={{
                                    flex: 1,
                                    border: "1px solid #ddd",
                                    borderRadius: "6px",
                                    background: "#fff",
                                    position: "relative",
                                    zIndex: 1000,
                                }}
                            >
                                <summary
                                    style={{
                                        height: "40px",
                                        padding: "0 8px",
                                        fontSize: "13px",
                                        cursor: "pointer",
                                        listStyle: "none",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        whiteSpace: "nowrap",
                                        gap: "10px",
                                    }}
                                >
                                    <span>{isVerySmall ? "Filter By Category" : "Filter By Category"}</span>
                                    <FaAngleDown />
                                </summary>

                                {/* FILTER DROPDOWN */}
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                        right: 0,
                                        background: "#fff",
                                        border: "1px solid #ddd",
                                        borderRadius: "6px",
                                        marginTop: "6px",
                                        padding: "10px",
                                        Height: "100%",
                                        zIndex: 1001,
                                    }}
                                >
                                    {categories.map((cat) => {
                                        const hasSub = cat.subCategories?.length > 0;
                                        const isOpen = openCategory === cat._id;

                                        return (
                                            <div key={cat._id} style={{ marginBottom: "8px" }}>
                                                {/* CATEGORY */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        fontWeight: "600",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => {
                                                        if (hasSub) {
                                                            setOpenCategory(isOpen ? null : cat._id);
                                                        } else {
                                                            navigate(`/category/${slugify(cat.name)}`);
                                                            setMobileFilterOpen(false);
                                                        }
                                                    }}
                                                >
                                                    <span>{cat.name}</span>
                                                    <span>{hasSub ? (isOpen ? "−" : "+") : ""}</span>
                                                </div>

                                                {/* SUBCATEGORY */}
                                                {hasSub &&
                                                    isOpen &&
                                                    cat.subCategories.map((sub) => (
                                                        <div
                                                            key={sub}
                                                            style={{
                                                                marginTop: "6px",
                                                                padding: "6px 10px",
                                                                fontSize: "13px",
                                                                background: "#f5f5f5",
                                                                borderRadius: "6px",
                                                                cursor: "pointer",
                                                            }}
                                                            onClick={() => {
                                                                navigate(
                                                                    `/category/${slugify(cat.name)}/${slugify(sub)}`
                                                                );
                                                                setMobileFilterOpen(false);
                                                            }}
                                                        >
                                                            {sub}
                                                        </div>
                                                    ))}
                                            </div>
                                        );
                                    })}
                                </div>
                            </details>

                            {/* ↕ SORT */}
                            <select
                                value={sortBy}
                                onMouseDown={() => setMobileFilterOpen(false)}
                                onChange={(e) => setSortBy(e.target.value)}
                                style={{
                                    flex: 1,
                                    height: "40px",
                                    padding: "0 8px",
                                    borderRadius: "6px",
                                    border: "1px solid #ddd",
                                    background: "#fff",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <option value="">
                                    {isVerySmall ? "Sort by" : "Sort by"}
                                </option>
                                <option value="price_low_high">Low → High</option>
                                <option value="price_high_low">High → Low</option>
                            </select>
                        </div>


                        {/* PRODUCTS */}
                        {loading ? (
                            <div style={{ textAlign: "center" }}>Loading...</div>
                        ) : finalProducts.length === 0 ? (
                            <div style={{ textAlign: "center" }}>No products found</div>
                        ) : (
                            <div className="product-grid">
                                {finalProducts.map((item) => (
                                    <div className="product-card" key={item._id}>
                                        <div className="image-container">
                                            <Link
                                                to={`/${slugify(item.category)}/${slugify(
                                                    item.subCategory
                                                )}/${slugify(item.title.replaceAll('/', ' '))}/${item._id}`}
                                            >
                                                <img
                                                    src={item.images?.[0] || "/fallback.jpg"}
                                                    alt={item.title}
                                                />
                                            </Link>

                                            <div className="rating-badge-co">
                                                <div className="rating-badgel">
                                                    <MdStar />
                                                    <h3>{item.averageRating}</h3>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="all-item">
                                            <p className="originale-priceo">
                                                {`${item.userId?.name || ""} ${item.userId?.lastName || "Unknown Seller"
                                                    }`}
                                            </p>

                                            <div className="sprat">
                                                <h3>{capitalizeWords(item.title)}</h3>
                                            </div>


                                            <p className="price-discounti">
                                                <span className="final-priceoe">
                                                    ₹
                                                    {calculateDiscountedPrice(
                                                        item.price,
                                                        item.discount
                                                    ).toLocaleString("en-IN")}
                                                </span>

                                                {item.discount > 0 && (
                                                    <>
                                                        |
                                                        <span className="discounte">{item.discount}% OFF</span>
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

            </div>
            <Footer />
        </>
    );
};

export default SearchPage;
